import { Filter as FilterIcon } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { filtersFromParams } from 'features/agent-analytics/utils/deepLinkFilters';
import axiosInstance from 'shared/services/api/axiosInstance';
import { dedupedGet } from 'shared/services/api/dedupedGet';
import TableSkeleton from 'shared/components/ui/TableSkeleton';
import AgentRunsTable from 'features/agent-analytics/components/AgentRunsTable';
import SemanticSearchBar from 'features/agent-analytics/components/SemanticSearchBar';
import AgentRunDetailPage from 'features/agent-analytics/pages/AgentRunDetailPage';
import AgentRunsQueryBuilder, {
  buildModularFilters,
} from 'features/agent-analytics/components/AgentRunsQueryBuilder';
import AgentRunsColumnsButton from 'features/agent-analytics/components/AgentRunsColumnsButton';
import RunsHistogram from 'features/agent-analytics/components/RunsHistogram';
import TracesViewTabs from 'features/agent-analytics/components/TracesViewTabs';
import EventsTimeFilter from 'features/activity/components/EventsTimeFilter';
import { DEFAULT_RUN_COLUMN_KEYS, resolveRunColumns } from 'features/agent-analytics/utils/runColumns';
import { useBreadcrumb } from 'shared/context/BreadcrumbContext';
import { usePreferences } from 'shared/context/preferencesContext';
import { trackClientEvent } from 'shared/utils/trodoEvents';
import {
  activityToolbarButtonClass,
} from 'features/activity/styles/dataTableStyles';
import TableFooter from 'shared/components/tables/TableFooter';
import { presetToISORange, DEFAULT_LIST_PRESET } from 'shared/utils/timePresets';
import { useAnnotations } from 'features/agent-analytics/annotations';
import BulkBar from 'shared/components/tables/BulkBar';
import AddToDatasetButton from 'features/datasets/components/AddToDatasetButton';
import SourceDialog from 'features/datasets/components/SourceDialog';
import { withEvalFilter, evalConditionCount, readEvalLines, writeEvalLines } from 'features/agent-analytics/components/EvalFilterSection';

const PAGE_SIZE = 25;

function useSelectedTeam() {
  const [team, setTeam] = useState(() =>
    typeof window === 'undefined' ? '' : localStorage.getItem('selectedTeam') || ''
  );
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'selectedTeam') setTeam(e.newValue || '');
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);
  return team;
}

/** Decode a JSON-encoded URL search param. Returns `[]` when missing/invalid. */
function readJsonParam(searchParams, key) {
  const raw = searchParams.get(key);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(decodeURIComponent(raw));
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

const AgentRunsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * The run, span or conversation the link that opened this page pointed at.
   *
   * The effect below rebuilds the query string from this page's own state, so
   * the first render after arriving from a notification threw these away —
   * the drawer had already captured the id, but the URL no longer said so,
   * and a reload or a shared copy of it opened nothing.
   *
   * Captured ONCE, on mount. Reading them from `searchParams` inside the
   * effect would mean depending on the thing the effect writes, which is an
   * infinite loop — and these are arrival values that never change anyway.
   */
  const [deepLinkParams] = useState(() => {
    const out = {};
    for (const k of ['run_id', 'span_id', 'span_started_at', 'conversation_id', 'tab']) {
      const v = searchParams.get(k);
      if (v) out[k] = v;
    }
    return out;
  });
  const teamId = useSelectedTeam();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Visual query builder state.
  const [selectedAgents, setSelectedAgents] = useState(() => readJsonParam(searchParams, 'agents'));
  const [agentScopedFilters, setAgentScopedFilters] = useState(() => readJsonParam(searchParams, 'agentFilters'));
  // Same as Traces: a deep link's plain params are filters too, so "Open in
  // Runs" under a chat answer arrives showing the answer's own rows.
  const [globalFilters, setGlobalFilters] = useState(() => {
    const fromJson = readJsonParam(searchParams, 'globalFilters');
    if (fromJson.length) return fromJson;
    return filtersFromParams(searchParams, 'runs');
  });

  // EVALS section of the filter: what the evals said about the run (URL `evals`).
  const [evalLines, setEvalLines] = useState(() => readEvalLines(searchParams));

  const [periodTotals, setPeriodTotals] = useState({ total: 0, errors: 0 });
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  // { candidates, kept } when the server dropped search hits that fell outside
  // the active filters — so a short result list can say why it is short.
  const [searchNarrowed, setSearchNarrowed] = useState(null);
  const [selectedRun, setSelectedRun] = useState(null);
  const [runDrawerTab, setRunDrawerTab] = useState(() => (
    searchParams.get('tab') === 'annotations' ? 'annotations' : null
  ));
  const [deepRunId] = useState(() => searchParams.get('run_id') || '');
  const [deepRunTab] = useState(() => searchParams.get('tab') || '');
  const { prefetchCounts } = useAnnotations();
  // Totals for the period, sourced from the histogram so the chip and chart
  // always stay in lockstep without a second query.

  // Memoize the modular-filter payload so child effects don't re-fire on every
  // unrelated parent render. Keyed by the actual filter contents (JSON of the
  // three lists), so identity stays stable until the filters truly change.
  const stableModularFilters = useMemo(
    () => withEvalFilter(buildModularFilters({ selectedAgents, agentScopedFilters, globalFilters }), evalLines),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(selectedAgents), JSON.stringify(agentScopedFilters), JSON.stringify(globalFilters), JSON.stringify(evalLines)]
  );

  // Breadcrumb shows the run_id (copyable) when a run is open, so the user can
  // copy it straight from the path.
  useBreadcrumb(
    selectedRun
      ? [
          { label: 'Traces', to: `/${teamId}/agent-analytics/runs` },
          { label: selectedRun.run_id || 'Run', copyable: true },
        ]
      : [{ label: 'Traces' }]
  );

  // Prev/next navigation through the loaded rows without closing the drawer.
  const selectedIndex = selectedRun
    ? rows.findIndex((r) => r.run_id === selectedRun.run_id)
    : -1;
  const gotoRunAt = (idx) => {
    if (idx >= 0 && idx < rows.length) setSelectedRun(rows[idx]);
  };

  // Show / hide for the AGENTS / FILTERS query builder. Defaults to open
  // when the URL already has any filters; collapsed otherwise.
  const [filterShown, setFilterShown] = useState(
    () =>
      readJsonParam(searchParams, 'agents').length +
        readJsonParam(searchParams, 'agentFilters').length +
        readJsonParam(searchParams, 'globalFilters').length +
        readEvalLines(searchParams).length >
      0,
  );

  // Date / time filter (Today, Yesterday, 7D, ..., Custom) — same component
  // Activity > Events uses. Stored as ISO strings; sent as flat
  // `time_start` / `time_end` query params alongside the modular filters.
  // Defaults to 30 days so the table actually filters on first load
  // (previously: empty preset → empty range → API returned everything).
  const [timePreset, setTimePreset] = useState(searchParams.get('preset') || DEFAULT_LIST_PRESET);
  const [timeRange, setTimeRange] = useState(() => {
    const seedPreset = searchParams.get('preset') || DEFAULT_LIST_PRESET;
    // For a relative preset (Today/7D/30D/…) ALWAYS recompute the window from the
    // preset so the upper bound is "now" on every load. Honouring the absolute
    // time_start/time_end that syncUrl froze into the URL would pin time_end to
    // when the page was first opened — hiding any run created since, until the
    // user logs out/in (which clears the URL). Only a 'custom' range keeps its
    // explicit timestamps.
    if (seedPreset && seedPreset !== 'custom') {
      const seeded = presetToISORange(seedPreset);
      if (seeded) return seeded;
    }
    const urlFrom = searchParams.get('time_start');
    const urlTo   = searchParams.get('time_end');
    if (urlFrom || urlTo) return { from: urlFrom || '', to: urlTo || '' };
    const seeded = presetToISORange(DEFAULT_LIST_PRESET);
    return seeded || { from: '', to: '' };
  });
  // Ticked runs → "Add to dataset…"; the page's filter → "Create dataset from this filter".
  const [checkedIds, setCheckedIds] = useState(() => new Set());
  const [datasetDialog, setDatasetDialog] = useState(false);
  useEffect(() => { setCheckedIds(new Set()); }, [selectedAgents, agentScopedFilters, globalFilters, evalLines, timeRange, searchQuery]);
  const toggleChecked = useCallback((id) => setCheckedIds((cur) => { const n = new Set(cur); if (n.has(id)) n.delete(id); else n.add(id); return n; }), []);
  const toggleAllChecked = useCallback((on) => setCheckedIds(on ? new Set(rows.map((r) => r.run_id)) : new Set()), [rows]);

  const onTimeFilterChange = (next) => {
    const preset = next?.preset || (next?.type === 'custom' ? 'custom' : '');
    setTimePreset(preset);
    // If the picker hands us explicit from/to, use them as-is.
    // Otherwise (preset-only payload), compute the range so the API gets a real window.
    if (next?.from || next?.to) {
      setTimeRange({ from: next?.from || '', to: next?.to || '' });
    } else {
      const seeded = presetToISORange(preset);
      setTimeRange(seeded || { from: '', to: '' });
    }
  };

  // Server-side column preferences via usePreferences. Falls back to defaults
  // until the load resolves.
  const {
    agentRunsTableColumns,
    loadAgentRunsTableColumns,
    updateAgentRunsTableColumns,
  } = usePreferences();
  useEffect(() => {
    if (teamId) loadAgentRunsTableColumns(teamId);
  }, [teamId, loadAgentRunsTableColumns]);

  const columnKeys = (agentRunsTableColumns?.enabled?.length
    ? agentRunsTableColumns.enabled
    : DEFAULT_RUN_COLUMN_KEYS);

  const onColumnsChange = (next) => {
    if (!teamId) return;
    const order = next;  // simple: order tracks enabled
    updateAgentRunsTableColumns(teamId, next, order).catch(() => {});
  };

  const requestIdRef = useRef(0);

  // Sync URL with the three filter lists + searchQuery + time range.
  const syncUrl = useCallback(() => {
    const next = new URLSearchParams();
    // Carry the deep-link keys through — see deepLinkParams.
    for (const [k, v] of Object.entries(deepLinkParams)) next.set(k, v);
    if (selectedAgents.length > 0) next.set('agents', encodeURIComponent(JSON.stringify(selectedAgents)));
    if (agentScopedFilters.length > 0) next.set('agentFilters', encodeURIComponent(JSON.stringify(agentScopedFilters)));
    if (globalFilters.length > 0) next.set('globalFilters', encodeURIComponent(JSON.stringify(globalFilters)));
    writeEvalLines(next, evalLines);
    if (searchQuery) next.set('q', searchQuery);
    if (timeRange.from) next.set('time_start', timeRange.from);
    if (timeRange.to)   next.set('time_end',   timeRange.to);
    if (timePreset)     next.set('preset', timePreset);
    setSearchParams(next, { replace: true });
  }, [selectedAgents, agentScopedFilters, globalFilters, evalLines, searchQuery, timeRange, timePreset, setSearchParams, deepLinkParams]);

  const loadRuns = useCallback(
    async (appendOffset = 0) => {
      if (!teamId) {
        setError('Select a team to view agent runs.');
        setRows([]);
        setLoading(false);
        return;
      }
      const id = ++requestIdRef.current;
      setLoading(true);
      setError(null);
      try {
        const modularFilters = withEvalFilter(buildModularFilters({ selectedAgents, agentScopedFilters, globalFilters }), evalLines);
        if (searchQuery) {
          // Search NARROWS the filters — it does not replace them. This call
          // used to send only team_id and q, so typing anything in the search
          // box silently dropped the agent selection, every filter chip and the
          // time range, while the chips stayed on screen claiming to be active.
          const params = { team_id: teamId, q: searchQuery, limit: 100 };
          if (modularFilters.length > 0) {
            params.modularFilters = encodeURIComponent(JSON.stringify(modularFilters));
          }
          if (timeRange.from) params.time_start = timeRange.from;
          if (timeRange.to)   params.time_end   = timeRange.to;
          const { data } = await axiosInstance.get('/agent-runs/search', { params });
          if (id !== requestIdRef.current) return;
          setRows(data?.runs || []);
          setTotal((data?.runs || []).length);
          setSearchNarrowed(data?.filtered || null);
          setHasMore(false);
          setOffset(0);
        } else {
          const params = { team_id: teamId, limit: PAGE_SIZE, offset: appendOffset };
          if (modularFilters.length > 0) {
            params.modularFilters = encodeURIComponent(JSON.stringify(modularFilters));
          }
          if (timeRange.from) params.time_start = timeRange.from;
          if (timeRange.to)   params.time_end   = timeRange.to;
          const { data } = await dedupedGet('/agent-runs', { params });
          setSearchNarrowed(null);
          if (id !== requestIdRef.current) return;
          const incoming = data?.runs || [];
          const totalCount = data?.pagination?.total ?? incoming.length;
          setRows((prev) => (appendOffset === 0 ? incoming : [...prev, ...incoming]));
          setTotal(totalCount);
          setHasMore(Boolean(data?.pagination?.hasMore));
          setOffset(appendOffset + incoming.length);
        }
      } catch (err) {
        if (id !== requestIdRef.current) return;
        setError(err?.response?.data?.error || err?.message || 'Failed to load runs.');
      } finally {
        if (id === requestIdRef.current) setLoading(false);
      }
    },
    [teamId, selectedAgents, agentScopedFilters, globalFilters, evalLines, searchQuery, timeRange]
  );

  useEffect(() => {
    syncUrl();
    loadRuns(0);
  }, [loadRuns, syncUrl]);

  const closeDrawer = () => setSelectedRun(null);
  const onRunChanged = () => loadRuns(0);

  useEffect(() => {
    if (!deepRunId) return;
    if (deepRunTab === 'annotations') setRunDrawerTab('activity');
    setSelectedRun((prev) => (prev?.run_id === deepRunId ? prev : { run_id: deepRunId }));
  }, [deepRunId, deepRunTab]);

  useEffect(() => {
    prefetchCounts({ runIds: rows.map((r) => r.run_id).filter(Boolean) });
  }, [rows, prefetchCounts]);

  const activeFilterCount =
    selectedAgents.length + agentScopedFilters.length + globalFilters.length + evalConditionCount(evalLines);

  return (
    <main
      className="flex flex-col bg-surface"
      style={{ fontFamily: 'Inter, sans-serif' }}
    >
      {/* Renders nothing here: injects the Conversations/Runs/Spans switcher
          into the top bar next to the breadcrumb. */}
      <TracesViewTabs value="runs" />
      <div className="flex flex-col gap-2 bg-surface px-3 sm:px-4 pb-2 pt-3">
        {/* Toolbar:
            time-period · totals · search · Filter ··· Edit columns
            (search is contained width; Edit columns sits at the right end) */}
        <div className="flex flex-wrap items-center gap-2">
          <EventsTimeFilter
            selectedPreset={timePreset || DEFAULT_LIST_PRESET}
            onTimeFilterChange={onTimeFilterChange}
          />
          {/* Period totals for the selected window, beside the dates */}
          {!searchQuery && (
            <div className="inline-flex h-8 items-stretch overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface">
              <div className="flex items-center gap-1.5 px-3 text-[12px]">
                <span className="font-semibold tabular-nums text-[color:var(--text-primary)]">
                  {periodTotals.total.toLocaleString()}
                </span>
                <span className="text-[color:var(--text-muted)]">Total</span>
              </div>
              <div className="flex items-center gap-1.5 border-l border-[color:var(--border-default)] px-3 text-[12px]">
                <span className="inline-block h-2 w-2 rounded-[2px] bg-[color:var(--feedback-error)]" aria-hidden />
                <span className="font-semibold tabular-nums text-[color:var(--text-primary)]">
                  {periodTotals.errors.toLocaleString()}
                </span>
                <span className="text-[color:var(--text-muted)]">Error</span>
              </div>
            </div>
          )}
          <SemanticSearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search runs or scores (ok=false)…"
            width={240}
          />
          <button
            type="button"
            onClick={() => setFilterShown((v) => !v)}
            className={activityToolbarButtonClass}
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            <FilterIcon size={14} color="var(--text-muted)" />
            Filter
            {activeFilterCount > 0 ? (
              <span className="rounded-full bg-surface-hover px-1.5 text-[10px] font-semibold text-[color:var(--text-primary)]">
                {activeFilterCount}
              </span>
            ) : null}
          </button>
          <button type="button" onClick={() => setDatasetDialog(true)} className={activityToolbarButtonClass}>
            Dataset from filter
          </button>
          <div className="ml-auto">
            <AgentRunsColumnsButton columnKeys={columnKeys} onChange={onColumnsChange} />
          </div>
        </div>

        <AgentRunsQueryBuilder
          teamId={teamId}
          selectedAgents={selectedAgents}
          onSelectedAgentsChange={setSelectedAgents}
          agentScopedFilters={agentScopedFilters}
          onAgentScopedFiltersChange={setAgentScopedFilters}
          globalFilters={globalFilters}
          onGlobalFiltersChange={setGlobalFilters}
          evalLines={evalLines}
          onEvalLinesChange={setEvalLines}
          shown={filterShown}
        />
      </div>

      <div className="flex shrink-0 flex-col gap-3 px-4 pb-8 pt-3">
        {/* Timeline histogram: same filters as the table */}
        {!searchQuery && (
          <RunsHistogram
            teamId={teamId}
            timeRange={timeRange}
            modularFilters={stableModularFilters}
            onTotals={setPeriodTotals}
          />
        )}

        {error ? (
          <div className="rounded-lg border border-[color:var(--feedback-error-bg)] bg-[color:var(--feedback-error-bg)] p-3 text-[12px] text-[color:var(--feedback-error)]">
            {error}
          </div>
        ) : null}

        {loading && rows.length === 0 ? (
          // resolveRunColumns is what the table itself renders, so the skeleton
          // and the real header cannot disagree about columns or order.
          <TableSkeleton
            columns={resolveRunColumns(columnKeys).map((c) => c.label)}
            rows={12}
          />
        ) : (
          <AgentRunsTable
            rows={rows}
            columnKeys={columnKeys}
            checkedIds={checkedIds}
            onToggleChecked={toggleChecked}
            onToggleAllChecked={toggleAllChecked}
            selectedRunId={selectedRun?.run_id || null}
            onRowClick={(run) => {
              trackClientEvent(
                'agent_run_opened',
                {
                  run_id: run?.id ?? run?.run_id ?? null,
                  agent_name: run?.agent_name ?? null,
                  status: run?.status ?? null,
                },
                { category: 'product' }
              );
              setRunDrawerTab(null);
              setSelectedRun(run);
            }}
            onAnnotationClick={(run) => {
              setRunDrawerTab('activity');
              setSelectedRun(run);
            }}
          />
        )}

        {/* Table footer: showing-N count on the left, Load more centered. */}
        {rows.length > 0 || total > 0 ? (
          <TableFooter
            shown={rows.length}
            total={total}
            hasMore={hasMore && !searchQuery}
            loading={loading}
            onLoadMore={() => loadRuns(offset)}
            note={(() => {
              if (loading && rows.length === 0) return 'Loading…';
              if (total === 0) return 'No runs match';
              // While searching, say what the search did RELATIVE to the
              // filters. A short list is otherwise indistinguishable from
              // "the search found little", which is what made search look
              // like it was ignoring the filters even now that it doesn't.
              if (searchQuery && searchNarrowed) {
                const { candidates, kept } = searchNarrowed;
                return kept < candidates
                  ? `${kept.toLocaleString()} of ${candidates.toLocaleString()} matches are inside your filters`
                  : `${kept.toLocaleString()} ${kept === 1 ? 'match' : 'matches'} inside your filters`;
              }
              if (searchQuery) {
                return `${rows.length.toLocaleString()} ${rows.length === 1 ? 'match' : 'matches'}`;
              }
              return null;
            })()}
          />
        ) : null}
      </div>

      <BulkBar count={checkedIds.size} label="Selected runs" onClear={() => setCheckedIds(new Set())}
        actions={[{ label: 'Create dataset from this filter', onClick: () => setDatasetDialog(true) }]}>
        <AddToDatasetButton selection={{ run_ids: [...checkedIds] }} onDone={() => setCheckedIds(new Set())} />
      </BulkBar>
      {datasetDialog ? (
        <SourceDialog newDataset
          initial={{ kind: 'runs', selectedAgents, agentScopedFilters, globalFilters, evalConditions: evalLines, time: { preset: timePreset, from: timeRange.from, to: timeRange.to } }}
          onClose={() => setDatasetDialog(false)}
        />
      ) : null}

      {selectedRun ? (
        <AgentRunDetailPage
          runId={selectedRun.run_id}
          teamId={teamId}
          initialTab={runDrawerTab || undefined}
          onClose={closeDrawer}
          onChange={onRunChanged}
          onPrev={() => gotoRunAt(selectedIndex - 1)}
          onNext={() => gotoRunAt(selectedIndex + 1)}
          hasPrev={selectedIndex > 0}
          hasNext={selectedIndex >= 0 && selectedIndex < rows.length - 1}
          // The table behind stays live: onRowClick swaps the drawer to that
          // run, and the row highlight (selectedRunId) already assumed you
          // could. A dismissing scrim closed the drawer on every such click.
          scrimDismisses={false}
        />
      ) : null}
    </main>
  );
};

export default AgentRunsPage;
